import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  effect,
  ChangeDetectorRef,
  afterRenderEffect,
  inject,
} from '@angular/core';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { Subscription, Observable, of, timer } from 'rxjs';
import {
  FormGroup,
  FormControl,
  Validators,
  FormGroupDirective,
  ControlContainer,
  AsyncValidatorFn,
  AbstractControl,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SimCardOptions } from '@sales/modules/subscription/components/subscription-stepper/number-chooser-step/SimCardOptions';

import { ErrorStateOnTouched } from '@app/material/ErrorStateOnTouched';
import { ErrorStateOnTouchedOrInvalidValue } from '@app/material/ErrorStateOnTouchedOrInvalidValue';
import { switchMap, tap, debounceTime, distinctUntilChanged, map, catchError, finalize, take } from 'rxjs/operators';
import { select, Store } from '@ngxs/store';

import { SimService } from '@sales/services/sim.service';
import { SimCardType } from '@sales/models/simcard.model';
import { SimCardValidator } from '@sales/validators/async-sim-card-validator';
import { FormStatus } from '@sales/models/form-status.enum';
import { CustomerProductSelectors } from '@sales/states/customer-product/customer-product.selectors';
import { CustomerSelectors } from '@sales/modules/details/states/customer.selectors';
import { CustomerService } from '@sales/modules/details/services/customer.service';

import { PhoneNumber } from '@shared/models/phone-number.model';
import { Regex } from '@shared/enums/regex';
import { IconNames } from '@shared/enums/icon-names';

import { ANumberResponse } from '../../../models/available-numbers-response.model';
import { PortingWindow } from '../../../services/porting.service';
import { DatePipe } from '@angular/common';
import { SubscriptionService } from '@sales/services/subscription.service';
import { TrialSubscription } from '@sales/validators/async-trial-subscription-validator';
import {
  SubscriptionDetailsSimplified,
  SubscriptionStatusSimplified,
  SubscriptionFamilyType,
} from '@shared/models/subscription-model';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { ShoppingCartSelectors } from '@app/sales/states/shopping-cart/shopping-cart.selectors';
import { DelayESimPorting } from '@app/sales/states/customer-product/customer-product.actions';
import { FormValidationService } from '@app/shared/services/form-validation.service';
import { dateFns } from '@app/core/date-utils';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { ActivationDateComponent } from './activation-date/activation-date.component';
import { EsimInfoModalComponent } from '../../../../../components/esim-info-modal/esim-info-modal.component';
import { IceSelectComponent } from '@app/shared/components/molecules/ice-select/ice-select.component';
import { InfoAccordionComponent } from '@app/shared/components/molecules/info-accordion/info-accordion.component';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { CustomerProductMobilePhoneSelectors } from '@app/sales/states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { SimTypeSupport } from '@app/sales/modules/mobilephone/models/sim-type-support';
import { TwoGSunsetInformationComponent } from './two-g-sunset-information/two-g-sunset-information.component';
import { updateFormAfterAsyncValidation } from '@app/core/utils';
import { MatIcon } from '@angular/material/icon';
import { eidValidator } from '@app/sales/modules/subscription/components/subscription-stepper/number-chooser-step/validators/EidValidator';
import { MatTooltip } from '@angular/material/tooltip';

interface NumberChooserForm {
  isPortingOrder: boolean;
  activationDateTime: string;
  portingTime: string;
  isOwnerChange: boolean;
  keepExistingSim: boolean;
  existingOwnerCustomerId: string;
  aNumberWithPorting: string;
  aNumberWithNewNumber: string;
  orderSim: boolean;
  isESim: boolean;
  icc: string;
  eid?: string;
}

@Component({
  selector: 'app-number-chooser-step',
  templateUrl: './number-chooser-step.component.html',
  styleUrls: ['./number-chooser-step.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    SpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    IceSelectBoxComponent,
    IceInputFieldComponent,
    IceAlertComponent,
    IceCardComponent,
    InfoAccordionComponent,
    IceSelectComponent,
    EsimInfoModalComponent,
    ActivationDateComponent,
    IceButtonComponent,
    DatePipe,
    TwoGSunsetInformationComponent,
    MatIcon,
    MatTooltip,
  ],
})
export class NumberChooserStepComponent implements OnInit, OnChanges, OnDestroy {
  private store = inject(Store);
  private formGroupDirective = inject(FormGroupDirective);
  private simService = inject(SimService);
  private subscriptionService = inject(SubscriptionService);
  private customerService = inject(CustomerService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  IconNames = IconNames;
  FormStatus = FormStatus;
  SimCardType = SimCardType;
  SimCardOptions = SimCardOptions;
  getMsisdnErrorText = FormValidationService.getMsisdnErrorText;
  getSimCardErrorTexts = FormValidationService.getSimCardErrorTexts;

  customerId = select(CustomerSelectors.customerId);
  hasSecretNumberAccess = select(DealerStateSelectors.hasSecretNumberAccess);
  isLoadingPortingDates = select(CustomerProductSelectors.isLoadingPortingDates);
  allowedPortingDates = select(CustomerProductSelectors.allowedPortingDates);
  getMsisdnsOnExistingSubscriptions = select(CustomerProductSelectors.getMsisdnsOnExistingSubscriptions);
  hasShoppingCartLogisticsVoiceProduct = select(ShoppingCartSelectors.hasShoppingCartLogisticsVoiceProduct);
  hasShoppingCartESimWithPortingTimeEarlierThanLogisticsVoiceProductAllows = select(
    ShoppingCartSelectors.hasShoppingCartESimWithPortingTimeEarlierThanLogisticsVoiceProductAllows
  );
  selectedSubscription = select(CustomerProductSelectors.selectedSubscription);
  bundleHardwareVariant = select(CustomerProductMobilePhoneSelectors.getMobilePhoneVariantInSelectedSubscriptionBundle);
  canRegisterEidClaim = select(DealerStateSelectors.canRegisterEidClaim);

  subscriptions$: Subscription[] = [];

  @ViewChild('eSimDelayWarning') eSimDelayWarningDialog: ElementRef<HTMLDialogElement>;

  @Input() product: VoiceProduct;

  @Output() isInvalid = new EventEmitter();
  @Output() updatePhoneNumber = new EventEmitter<{ phoneNumberValues: PhoneNumber; productId: string }>();

  invalidOnTouched = new ErrorStateOnTouched();
  invalidValueOrTouched = new ErrorStateOnTouchedOrInvalidValue();

  datePipe = new DatePipe('nb');
  portingTimes: string[] = [];

  form: FormGroup;
  isPhysicalSimControl: FormControl;
  initialANumber: string;
  availableNumbers: ANumberResponse;

  isEarliestPortingDate = true;

  private lastChangedControl: 'porting' | 'orderSim';
  private isLoadingANumbers: boolean = false;

  static phoneNumberValidator = store => {
    return (control: AbstractControl): ValidationErrors | null => {
      const msisdns = store.selectSnapshot(CustomerProductSelectors.getMsisdnsOnExistingSubscriptions);

      if (!msisdns || msisdns.length === 0) {
        return null;
      }

      return msisdns.some((msisdn: string) => msisdn === control.value) ? { customerAlreadyOwnsMsisdn: true } : null;
    };
  };

  static isDateAllowedValidator = store => {
    return (form: FormGroup): ValidationErrors | null => {
      const portingDatesResponse = store.selectSnapshot(CustomerProductSelectors.allowedPortingDates);

      if (!portingDatesResponse) {
        return { invalidActivationDate: true };
      }

      const { isPortingOrder, orderSim, activationDateTime } = form.value;

      if (isPortingOrder) {
        const allowedDates = orderSim ? portingDatesResponse.withLogistics : portingDatesResponse.withoutLogistics;
        const isDateAllowed = allowedDates.some(
          (allowedDate: PortingWindow) =>
            new Date(allowedDate.date).toDateString() === new Date(activationDateTime).toDateString()
        );

        return isDateAllowed ? null : { invalidActivationDate: true };
      }

      const minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      const maxDate = new Date(new Date().setMonth(minDate.getMonth() + 2));

      const isDateAllowed = new Date(activationDateTime) >= minDate && new Date(activationDateTime) <= maxDate;

      return isDateAllowed ? null : { invalidActivationDate: true };
    };
  };

  static isRepeatedIccValidator = (productId: string, store) => {
    return (control: AbstractControl): ValidationErrors | null => {
      const newVoiceProducts = store.selectSnapshot(CustomerProductSelectors.getNewVoiceProducts);
      const isProductsComparable = newVoiceProducts && newVoiceProducts.length > 1;

      if (isProductsComparable) {
        const voiceProductsWithIcc = newVoiceProducts.filter(
          (newVoiceProduct: VoiceProduct) => newVoiceProduct.id !== productId && newVoiceProduct.phoneNumber?.icc
        );
        const isRepeatedIcc = voiceProductsWithIcc.some(
          (voiceProductWithIcc: VoiceProduct) => voiceProductWithIcc.phoneNumber.icc === control.value
        );

        return isRepeatedIcc ? { simCardRepeated: true } : null;
      }

      return null;
    };
  };

  static buildNumberChooserFormGroup(
    productId: string,
    icc: string,
    orderSim: boolean,
    isESim: boolean,
    isPorting: boolean,
    isOwnerChange: boolean,
    keepExistingSim: boolean,
    existingOwnerCustomerId: string,
    aNumber: string,
    activationDateTime: Date,
    portingTime: string,
    isEarliestPortingDate: boolean,
    store: Store,
    iccValidator?: AsyncValidatorFn,
    subscriptionService?: SubscriptionService,
    voiceProduct?: VoiceProduct,
    customerService?: CustomerService,
    simCardOptions?: SimCardOptions,
    eid?: string
  ) {
    var form = new FormGroup(
      {
        icc: new FormControl(
          {
            value: icc,
            disabled: orderSim || isESim || keepExistingSim,
          },
          [
            Validators.required,
            Validators.minLength(19),
            Validators.maxLength(20),
            Validators.pattern(Regex.ICC),
            NumberChooserStepComponent.isRepeatedIccValidator(productId, store),
          ],
          iccValidator ? [iccValidator] : []
        ),
        orderSim: new FormControl(orderSim),
        isESim: new FormControl(isESim),
        isPortingOrder: new FormControl(isPorting),
        eid: new FormControl(eid, [eidValidator()]),
        aNumberWithPorting: new FormControl(
          {
            value: aNumber,
            disabled: !isPorting,
          },
          [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(8),
            Validators.pattern(Regex.Phone),
            NumberChooserStepComponent.phoneNumberValidator(store),
          ],
          [...(voiceProduct && customerService ? [TrialSubscription.isValid(customerService, voiceProduct?.id, store)] : [])]
        ),
        isOwnerChange: new FormControl(isOwnerChange),
        keepExistingSim: new FormControl(keepExistingSim),
        existingOwnerCustomerId: new FormControl(existingOwnerCustomerId),
        aNumberWithNewNumber: new FormControl({ value: aNumber, disabled: isPorting }, [
          Validators.required,
          Validators.pattern(Regex.Phone),
        ]),
        activationDateTime: new FormControl(activationDateTime, [Validators.required]),
        portingTime: new FormControl(
          {
            value: portingTime,
            disabled: !isPorting || isEarliestPortingDate,
          },
          [Validators.required]
        ),
        simCardOptions: new FormControl(simCardOptions),
      },
      NumberChooserStepComponent.isDateAllowedValidator(store)
    );
    if (subscriptionService) {
      form.controls.aNumberWithPorting.setAsyncValidators([
        MsisdnValidator(subscriptionService, { isOwnerChange, keepExistingSim }, form),
      ]);
    }
    return form;
  }

  constructor() {
    effect(() => {
      const availableNumbersForSelectedSubscription = this.selectedSubscription()?.phoneNumber.availableNumbers;

      if (!availableNumbersForSelectedSubscription?.aNumbers.length && !this.isLoadingANumbers) {
        this.isLoadingANumbers = true;
        this.simService
          .availableNumbers(this.customerId())
          .pipe(take(1))
          .subscribe(aNumberResponse => {
            this.setANumbers(aNumberResponse);
            this.setAvailableNumbersForSelectedSubscription(aNumberResponse);
            this.isLoadingANumbers = false;
          });
      } else {
        this.setANumbers(availableNumbersForSelectedSubscription);
      }
    });
    afterRenderEffect(() => {
      const dialog = this.eSimDelayWarningDialog?.nativeElement;
      if (
        this.hasShoppingCartESimWithPortingTimeEarlierThanLogisticsVoiceProductAllows() &&
        this.isPhysicalSimControl?.value &&
        dialog?.isConnected &&
        !dialog.open
      ) {
        dialog.showModal();
      }
    });
  }

  get isPatternError(): boolean {
    return this.form.controls.aNumberWithPorting.errors?.pattern;
  }

  get isInvalidNumberError(): boolean {
    const { connectedSubscriptionInChangeState, customerAlreadyOwnsMsisdn, blockedSubscription } = this.form.controls;
    return (
      !this.hasUsedTrialSubscriptionOnANumber &&
      !this.isInRecentIceCustomerQuarantine &&
      !connectedSubscriptionInChangeState &&
      !customerAlreadyOwnsMsisdn &&
      !blockedSubscription
    );
  }

  get hasUsedTrialSubscriptionOnANumber(): boolean {
    return this.form.controls.aNumberWithPorting.errors?.hasUsedTrialSubscriptionOnANumber;
  }

  get isInRecentIceCustomerQuarantine(): boolean {
    return this.form.controls.aNumberWithPorting.errors?.isInRecentIceCustomerQuarantine;
  }

  get trialSubscriptionErrorMessage(): string {
    if (this.hasUsedTrialSubscriptionOnANumber) {
      return 'Det er mindre enn 24 måneder siden dette nummeret sist var knyttet til et "Prøv Oss"-abonnement.';
    }

    if (this.isInRecentIceCustomerQuarantine) {
      return 'Det er mindre enn 6 måneder siden dette nummeret sist var knyttet til et ice-abonnement.';
    }

    return '';
  }

  get isDelayedESim(): boolean {
    return !this.isPhysicalSimControl?.value && this.hasShoppingCartLogisticsVoiceProduct();
  }

  get isOnlySupportingEsim() {
    return this.bundleHardwareVariant()?.simTypeSupport === SimTypeSupport.Esim;
  }

  get isOnlySupportingPsim() {
    return this.bundleHardwareVariant()?.simTypeSupport === SimTypeSupport.Psim;
  }

  get displayEIDForm(): boolean {
    const isEsim = this.form?.controls?.isESim?.value;
    const isNewNumber = !this.form?.controls?.isPortingOrder?.value;
    const isSubscriptionConnectedToAPhone = !!this.bundleHardwareVariant();

    return (
      isEsim && isNewNumber && this.isEarliestPortingDate && isSubscriptionConnectedToAPhone && this.canRegisterEidClaim()
    );
  }

  get simTypeCompatibilityText(): string {
    const simTypeSupport = this.bundleHardwareVariant()?.simTypeSupport;

    switch (simTypeSupport) {
      case SimTypeSupport.Esim:
        return `Telefonen ${this.bundleHardwareVariant().groupName} støtter kun eSIM`;
      case SimTypeSupport.Psim:
        return `Telefonen ${this.bundleHardwareVariant().groupName} støtter kun fysisk SIM`;
      case SimTypeSupport.Both:
        return `Telefonen ${this.bundleHardwareVariant().groupName} støtter eSIM og fysisk SIM`;
      default:
        return '';
    }
  }

  ngOnInit() {
    this.form = this.buildForm(this.product);
    this.isEarliestPortingDate = this.product.phoneNumber.isEarliestPortingDate ?? true;

    const { isESim, simCardOptions, icc, orderSim, keepExistingSim, isOwnerChange, aNumberWithPorting, isPortingOrder } =
      this.form.controls;

    this.isPhysicalSimControl = new FormControl(!isESim?.value && !isOwnerChange?.value);
    this.subscriptions$.push(
      this.isPhysicalSimControl.valueChanges.subscribe(isPhysicalSim => {
        if (isPhysicalSim) {
          isESim.patchValue(false);
          simCardOptions.patchValue(SimCardOptions.RegisterNew);
          icc.enable();
        }
      })
    );

    this.subscriptions$.push(this.form.statusChanges.pipe(tap(() => this.isInvalid.emit(this.form.invalid))).subscribe());

    this.subscriptions$.push(
      simCardOptions.valueChanges.subscribe((simCardOptions: SimCardOptions) => {
        switch (simCardOptions) {
          case SimCardOptions.ESim:
            this.isPhysicalSimControl.patchValue(false);
            isESim.patchValue(true);
            orderSim.patchValue(false);
            keepExistingSim.patchValue(false);
            icc.disable();
            icc.patchValue(null);
            break;

          case SimCardOptions.SendToMail:
            orderSim.patchValue(true);
            isESim.patchValue(false);
            keepExistingSim.patchValue(false);
            icc.disable();
            icc.patchValue(null);
            break;

          case SimCardOptions.KeepExisting:
            keepExistingSim.patchValue(true);
            this.isPhysicalSimControl.patchValue(false);
            orderSim.patchValue(false);
            isESim.patchValue(false);
            icc.disable();
            icc.patchValue(null);
            break;

          case SimCardOptions.RegisterNew:
            orderSim.patchValue(false);
            isESim.patchValue(false);
            keepExistingSim.patchValue(false);
            icc.enable();
            break;

          default:
            break;
        }

        this.clearEidWhenNotApplicable();
      })
    );

    this.subscriptions$.push(
      isOwnerChange.valueChanges.subscribe((isOwnerChange: boolean) => {
        const isInitialState = !icc.value && !orderSim.value && !isESim.value && !keepExistingSim.value;

        if (isOwnerChange && isInitialState) {
          simCardOptions.patchValue(SimCardOptions.KeepExisting);
        } else if (!isOwnerChange && keepExistingSim.value) {
          simCardOptions.patchValue(SimCardOptions.RegisterNew);
        }
      })
    );

    this.isInvalid.emit(this.form.invalid);

    this.subscriptions$.push(
      aNumberWithPorting.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap(() => {
            if (aNumberWithPorting.invalid && isOwnerChange.value) {
              isOwnerChange.patchValue(false);
            }
          })
        )
        .subscribe()
    );

    this.subscriptions$.push(
      isPortingOrder.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap((isPortingOrder: boolean) => {
            this.lastChangedControl = 'porting';

            const { isOwnerChange, existingOwnerCustomerId, aNumberWithPorting, aNumberWithNewNumber } = this.form.controls;
            if (isPortingOrder === this.product.phoneNumber?.isPortingOrder) {
              return;
            }

            isOwnerChange.patchValue(false);
            existingOwnerCustomerId.patchValue(null);

            if (isPortingOrder) {
              aNumberWithPorting.patchValue(null);
              aNumberWithPorting.enable();

              aNumberWithNewNumber.patchValue(null);
              aNumberWithNewNumber.disable();
            } else {
              aNumberWithNewNumber.patchValue(this.initialANumber);
              aNumberWithNewNumber.enable();

              aNumberWithPorting.patchValue(null);
              aNumberWithPorting.disable();
            }

            this.clearEidWhenNotApplicable();
          })
        )
        .subscribe()
    );

    this.subscriptions$.push(
      orderSim.valueChanges.subscribe(() => {
        this.lastChangedControl = 'orderSim';
      })
    );

    this.subscriptions$.push(
      this.form.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((formValues: NumberChooserForm) => {
        this.updatePhoneNumberFromNumberChooserFormValues(formValues);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const formGroup = this.formGroupDirective.form.get('numberChooser') as FormGroup;
    const isExistingCustomer = this.store.selectSnapshot(CustomerSelectors.isExistingCustomer);
    const hasOfferingChanged =
      changes.product.previousValue &&
      changes.product.currentValue &&
      changes.product.previousValue.offering.id &&
      changes.product.previousValue.offering.id !== changes.product.currentValue.offering.id;

    if (isExistingCustomer && hasOfferingChanged && formGroup?.controls.isPortingOrder.value) {
      formGroup.controls.aNumberWithPorting.updateValueAndValidity();
    }
  }

  private buildForm(product: VoiceProduct): FormGroup {
    if (this.formGroupDirective.form.get('numberChooser')) {
      this.formGroupDirective.form.removeControl('numberChooser');
    }
    const {
      icc,
      orderSim,
      isESim,
      isPortingOrder,
      isOwnerChange,
      existingOwnerCustomerId,
      keepExistingSim,
      aNumber,
      activationDateTime,
      portingTime,
      isEarliestPortingDate,
    } = product.phoneNumber;

    this.formGroupDirective.form.addControl(
      'numberChooser',
      NumberChooserStepComponent.buildNumberChooserFormGroup(
        product.id,
        icc,
        orderSim,
        isESim || this.bundleHardwareVariant()?.simTypeSupport === SimTypeSupport.Esim,
        isPortingOrder,
        isOwnerChange,
        keepExistingSim,
        existingOwnerCustomerId,
        aNumber,
        activationDateTime,
        portingTime,
        isEarliestPortingDate,
        this.store,
        null,
        null,
        null,
        null,
        this.isOnlySupportingEsim ? SimCardOptions.ESim : SimCardOptions.RegisterNew,
        product.phoneNumber.eid
      )
    );

    const formGroup = this.formGroupDirective.form.get('numberChooser') as FormGroup;

    if (formGroup.controls.isESim?.value) {
      formGroup.controls.simCardOptions.patchValue(SimCardOptions.ESim);
    } else if (formGroup.controls.orderSim.value) {
      formGroup.controls.simCardOptions.patchValue(SimCardOptions.SendToMail);
    } else if (formGroup.controls.keepExistingSim.value) {
      formGroup.controls.simCardOptions.patchValue(SimCardOptions.KeepExisting);
    } else {
      formGroup.controls.simCardOptions.patchValue(SimCardOptions.RegisterNew);
    }

    formGroup.controls.icc?.setAsyncValidators(
      SimCardValidator(this.simService, SimCardType.Voice, () => {
        formGroup.updateValueAndValidity();
        this.isInvalid.emit(formGroup.invalid);
      })
    );

    formGroup.controls.aNumberWithPorting.setAsyncValidators([
      MsisdnValidator(
        this.subscriptionService,
        { isOwnerChange: this.product.phoneNumber.isOwnerChange, keepExistingSim: this.product.phoneNumber.keepExistingSim },
        formGroup,
        () => {
          this.formGroupDirective.form.updateValueAndValidity();
          this.isInvalid.emit(formGroup.invalid);
        }
      ),
      TrialSubscription.isValid(this.customerService, this.product?.id, this.store, () => {
        this.formGroupDirective.form.updateValueAndValidity();
        this.isInvalid.emit(formGroup.invalid);
      }),
    ]);

    return formGroup;
  }

  preventESimChange(): void {
    this.eSimDelayWarningDialog.nativeElement.close();
    if (this.lastChangedControl === 'porting') this.form.controls.isPortingOrder.setValue(false);
    if (this.lastChangedControl === 'orderSim') this.form.controls.simCardOptions.setValue(SimCardOptions.RegisterNew);
  }
  confirmESimChange(): void {
    this.eSimDelayWarningDialog.nativeElement.close();
    this.store.dispatch(new DelayESimPorting(new Date(this.allowedPortingDates().withLogistics[0].date)));
  }

  private setAvailableNumbersForSelectedSubscription(aNumberResponse: ANumberResponse): void {
    const phoneNumberValues = this.getPhoneNumberFromFormValues(this.form.value);
    this.updatePhoneNumber.emit({
      phoneNumberValues: { ...phoneNumberValues, availableNumbers: aNumberResponse },
      productId: this.product.id,
    });
  }

  private setANumbers(availableNumbers: ANumberResponse): void {
    if (!availableNumbers) return;

    const [aNumber] = availableNumbers.aNumbers;

    this.availableNumbers = availableNumbers;
    this.initialANumber = aNumber.number;

    if (!this.product.phoneNumber.aNumber && !this.product.phoneNumber.isPortingOrder) {
      this.form?.controls?.aNumberWithNewNumber.patchValue(aNumber.number);
    }

    this.changeDetectorRef.detectChanges();
  }

  private clearEidWhenNotApplicable(): void {
    if (!this.form) {
      return;
    }

    const eidControl = this.form.controls?.eid as FormControl;

    if (!eidControl) {
      return;
    }

    if (!this.displayEIDForm && eidControl.value) {
      eidControl.patchValue(null);
    }
  }

  private updatePhoneNumberFromNumberChooserFormValues(formValues: NumberChooserForm) {
    const phoneNumberValues = this.getPhoneNumberFromFormValues(formValues);
    this.updatePhoneNumber.emit({ phoneNumberValues: phoneNumberValues, productId: this.product.id });
  }

  onEarliestPortingDateChange(isEarliestPortingDate: boolean): void {
    this.isEarliestPortingDate = isEarliestPortingDate;
    this.clearEidWhenNotApplicable();
  }

  private getPhoneNumberFromFormValues(formValues: NumberChooserForm): PhoneNumber {
    if (!formValues) {
      return;
    }

    const {
      isPortingOrder,
      activationDateTime,
      portingTime,
      isOwnerChange,
      keepExistingSim,
      existingOwnerCustomerId,
      aNumberWithPorting,
      aNumberWithNewNumber,
      orderSim,
      isESim,
      icc,
      eid,
    } = formValues;

    const updatedActivationDateTime =
      isPortingOrder && activationDateTime && portingTime
        ? dateFns.getDateTimeWithSetTime(activationDateTime, portingTime)
        : dateFns.getDateTimeWithSetTime(activationDateTime, '06:00');

    return {
      aNumber: isPortingOrder ? aNumberWithPorting : aNumberWithNewNumber,
      activationDateTime: updatedActivationDateTime,
      isEarliestPortingDate: this.isEarliestPortingDate,
      isPortingOrder: isPortingOrder,
      isOwnerChange: isOwnerChange,
      keepExistingSim: keepExistingSim,
      existingOwnerCustomerId: existingOwnerCustomerId,
      portingTime: portingTime,
      orderSim: orderSim,
      isESim: isESim,
      icc: icc,
      availableNumbers: this.availableNumbers,
      eid,
    } as PhoneNumber;
  }

  get orderSimControl(): FormControl {
    return this.form.controls.orderSim as FormControl;
  }

  get isPortingOrderControl(): FormControl {
    return this.form.controls.isPortingOrder as FormControl;
  }

  get activationDateTimeControl(): FormControl {
    return this.form.controls.activationDateTime as FormControl;
  }

  get portingDateErrorText(): string {
    if (this.activationDateTimeControl.hasError('matDatepickerParse')) {
      return this.activationDateTimeControl.getError('matDatepickerParse').text;
    }
    if (
      this.activationDateTimeControl.hasError('matDatepickerFilter') ||
      this.activationDateTimeControl.hasError('invalidActivationDate')
    ) {
      return `Datoen du har valgt er ikke tillatt`;
    }
  }

  get aNumberOptions(): string[] {
    return this.availableNumbers?.aNumbers.map(aNumber => aNumber.number);
  }

  get earliestDateTime(): Date {
    const withLogisticsDate = this.allowedPortingDates()?.withLogistics[0]?.date ?? null;
    const withoutLogisticsDate = this.allowedPortingDates()?.withoutLogistics[0]?.date ?? null;
    const isOrderOrDelayedSim = this.form.controls.orderSim.value || this.isDelayedESim;

    return new Date(isOrderOrDelayedSim ? withLogisticsDate : withoutLogisticsDate);
  }

  get allowedPickerDates(): Date[] {
    if (this.form.controls.isPortingOrder.value || this.isDelayedESim) {
      return this.form.controls.orderSim.value || this.isDelayedESim
        ? this.allowedPortingDates()?.withLogistics?.map(portingWindow => new Date(portingWindow.date))
        : this.allowedPortingDates()?.withoutLogistics?.map(portingWindow => new Date(portingWindow.date));
    }
    return [];
  }

  get eidLongHelpText(): string {
    return 'Brukes hvis kunden ønsker aktivering så fort som mulig. EID-koden aktiverer mobilens innebygde eSIM og forenkler eSIM-installasjonen';
  }

  ngOnDestroy() {
    this.updatePhoneNumberFromNumberChooserFormValues(this.form.value);
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}

export function MsisdnValidator(
  subscriptionService: SubscriptionService,
  existingValues: any,
  numberChooserForm: FormGroup,
  onCompleteCallback: () => void = null
): AsyncValidatorFn {
  return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    return timer(500).pipe(
      switchMap(() => {
        if (!numberChooserForm) return null;
        var { isOwnerChange, existingOwnerCustomerId, orderSim, icc } = numberChooserForm.controls;
        return subscriptionService.getSubscriptionStatus(control.value).pipe(
          map((subscriptionDetailsSimplified: SubscriptionDetailsSimplified) => {
            if (
              subscriptionDetailsSimplified.subscriptionFamilyType === SubscriptionFamilyType.VoiceB2BAksessnummerMobil ||
              subscriptionDetailsSimplified.subscriptionFamilyType ===
                SubscriptionFamilyType.VoiceB2BAksessnummerMobilBasisTotal
            ) {
              isOwnerChange.patchValue(false);
              return { isSubscriptionIsAccessNumber: true };
            }
            if (!subscriptionDetailsSimplified.canEdit) {
              isOwnerChange.patchValue(false);
              existingOwnerCustomerId.patchValue(null);
              return { blockedSubscription: true };
            }
            if (subscriptionDetailsSimplified?.status === SubscriptionStatusSimplified.Active) {
              isOwnerChange.patchValue(true);
              if (existingValues.isOwnerChange && existingValues.keepExistingSim) {
                orderSim.disable();
              } else if (!existingValues.isOwnerChange) {
                // update value only  if it is not already updated. this is to avoid resetting values when user
                // navigates to a different view and navigates back without making any changes
                existingOwnerCustomerId.patchValue(subscriptionDetailsSimplified.customerId);
              }
            } else {
              isOwnerChange.patchValue(false);
              existingOwnerCustomerId.patchValue(null);
              if (orderSim.disabled) {
                orderSim.enable();
                icc.enable();
              }
              if (subscriptionDetailsSimplified?.status === SubscriptionStatusSimplified.Processing) {
                // return error if msisdn is in flight
                return { connectedSubscriptionInChangeState: true };
              }
            }

            return null;
          }),
          catchError(() => of({ apiException: true })),
          finalize(() => {
            updateFormAfterAsyncValidation(control);

            if (onCompleteCallback) {
              onCompleteCallback();
            }
          })
        );
      })
    );
  };
}
